import throttle from 'lodash/throttle';

// 上传完成后，调用 onProgress 前的最大等待时间
const UPLOADED_MAX_WAITING = 500;

// 最大同时上传的文件大小
const MAX_INFLIGHT_SIZE = 2e6;

// 最大同时上传数
const MAX_INFLIGHT_IMAGES = 3;

// 最大重试次数
const MAX_RETRY_COUNT = 2;

const UPLOAD_RETRY_INTERVAL = 3000;

export interface CanUpload<T> {
    getSize?: () => number;
    upload(): Promise<T>;
}

class UploadQueue<T> {
    private doneItems: T[] = [];
    private doneTasks: CanUpload<T>[] = [];
    private pendingTasks = new Set<CanUpload<T>>();
    private taskRetryCount = new Map<CanUpload<T>, number>();

    private doneNum = 0;
    private totalNum = 0;
    private isUploading = false;

    public enqueue(tasks: CanUpload<T>[], timeout = -1) {
        const schedule = callback => (timeout < 0 ? callback() : setTimeout(callback, timeout));

        const uniqueTasks = new Set(tasks);
        this.totalNum += uniqueTasks.size;

        schedule(() => {
            uniqueTasks.forEach(task => this.pendingTasks.add(task));
            this.uploadNextBatch();
        });
    }

    public onError(handler: (err: Error, item: CanUpload<T>) => void) {
        this.handleError = handler;
    }

    public onFinish(handler: () => void) {
        this.handleFinish = handler;
    }

    public onProgress(handler: (doneItems: T[]) => void) {
        this.handleProgress = throttle(
            () => {
                handler(this.doneItems);
                this.done(this.doneTasks);
                this.doneItems = [];
                this.doneTasks = [];
            },
            UPLOADED_MAX_WAITING,
            {
                leading: false,
            },
        );
    }

    private async uploadNextBatch() {
        if (!this.pendingTasks.size || this.isUploading) {
            return;
        }

        const getSize = (x: CanUpload<T>) => x.getSize?.() ?? 1e6;
        const sortedTasks = Array.from(this.pendingTasks).sort((a, b) => getSize(a) - getSize(b));

        let currentSize = getSize(sortedTasks[0]);
        let i = 1;

        while (currentSize < MAX_INFLIGHT_SIZE && i < sortedTasks.length && i < MAX_INFLIGHT_IMAGES) {
            currentSize += getSize(sortedTasks[i]);
            i += 1;
        }

        const nextBatch = sortedTasks.slice(0, i);

        try {
            this.isUploading = true;

            await Promise.all(nextBatch.map((task) => {
                this.pendingTasks.delete(task);
                return task
                    .upload()
                    .then(item => this.handleUploadSuccess(item, task))
                    .catch(err => this.handleUploadError(err, task));
            }));
        } finally {
            this.isUploading = false;
            this.uploadNextBatch();
        }
    }

    private handleError: (err: Error, task: CanUpload<T>) => void = () => {};
    private handleProgress: () => void = () => {};
    private handleFinish: () => void = () => {};

    private handleUploadSuccess = (item: T, task: CanUpload<T>) => {
        this.doneItems.push(item);
        this.doneTasks.push(task);
        this.taskRetryCount.delete(task);
        this.handleProgress();
    };

    private handleUploadError = (e: Error & { skipRetry?: boolean }, task: CanUpload<T>) => {
        const retryCount = this.taskRetryCount.get(task) ?? 0;
        if (retryCount < MAX_RETRY_COUNT && !e.skipRetry) {
            this.taskRetryCount.set(task, retryCount + 1);
            // 这里间隔一段时间再发起重试，否则在 Safari Unload 过程中，会杀死上传任务，导致图片连续上传失败，最终落盘为裂图的问题
            this.enqueue([task], UPLOAD_RETRY_INTERVAL);
        } else {
            this.taskRetryCount.delete(task);
            this.handleError(e, task);
        }
        this.done([task]);
    };

    private done(tasks: CanUpload<T>[]) {
        this.doneNum += tasks.length;
        if (this.doneNum === this.totalNum) {
            this.handleFinish();
        }
    }
}

export default UploadQueue;
